import { useEffect, useState } from 'react';
import Link from 'next/link';

import Button from '@components/atoms/button';
import SearchInput from '@components/SearchInput';
import {
  BodyRegular,
  BodySmallRegular,
  BodySmallRegularBold,
  CaptionRegular
} from '@components/atoms/typography';
import {
  searchFplEntries,
  type FplEntrySearchResult
} from '@api-service/fpl-entries';
import { isPlannerNotFound } from '@api-service/planner';
import { useLeagueContext } from '@hooks/useLeagueContext';

const MIN_TEAM_NAME_LENGTH = 3;

const isFplId = (value: string) => /^\d+$/.test(value);

const IMPORT_TIMED_OUT_MESSAGE =
  'The import is taking longer than expected. Please try again in a few minutes.';
const IMPORT_FAILED_MESSAGE =
  "We couldn't import your team just now. Please try again in a few minutes.";

const LATEST_ARTICLES_PATH = '/category/fpl';

// FPL only serves an entry once a deadline has passed since it was created,
// so a not-found is most likely a team made after the most recent deadline.
// The gameweek numbers are interpolated from league context; the wordless
// fallback covers the unlikely case of the context being unavailable.
const getTeamNotFoundMessage = (
  lastDeadlineGameweekNumber: number | null
): string => {
  const createdAfter =
    lastDeadlineGameweekNumber !== null
      ? `the GW${lastDeadlineGameweekNumber} deadline`
      : 'the most recent deadline';
  const worksFrom =
    lastDeadlineGameweekNumber !== null
      ? `GW${lastDeadlineGameweekNumber + 1}`
      : 'the next gameweek';
  return (
    `We couldn't find your team. If your team was created after ` +
    `${createdAfter}, our tools won't work until ${worksFrom}. Please try ` +
    'again after the next deadline.'
  );
};

const isTimeoutError = (error: unknown): boolean =>
  error instanceof DOMException &&
  (error.name === 'TimeoutError' || error.name === 'AbortError');

type GameweekWithDeadline = { number: number; deadlineAt: string };

const hasDeadlinePassed = (
  gameweek: GameweekWithDeadline | null
): gameweek is GameweekWithDeadline =>
  gameweek !== null && new Date(gameweek.deadlineAt).getTime() <= Date.now();

type TeamImportPanelProps = {
  onImport: (fplTeamId: number) => Promise<void> | void;
  isImporting: boolean;
  description: string;
  helpText?: string;
  onImported?: () => void;
  onBusyChange?: (isBusy: boolean) => void;
};

/**
 * FPL team linking flow: a single input that accepts either a numeric FPL ID
 * (imported directly) or a team name (searched, with a selectable results
 * list). Shared by TeamImportModal and the full-page FplLinkGate.
 */
export const TeamImportPanel = ({
  onImport,
  isImporting,
  description,
  helpText,
  onImported,
  onBusyChange
}: TeamImportPanelProps) => {
  const { context } = useLeagueContext();

  const [inputValue, setInputValue] = useState('');
  const [searchError, setSearchError] = useState<string | null>(null);
  // Not-found gets richer treatment than the other failures: the deadline
  // explanation plus a pointer at the articles that stay available meanwhile.
  const [isTeamNotFoundError, setIsTeamNotFoundError] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [results, setResults] = useState<FplEntrySearchResult[] | null>(null);
  const [importingEntryId, setImportingEntryId] = useState<number | null>(null);

  // The gameweek whose deadline most recently passed. currentGameweek lags
  // FPL's own rollover (isCurrent flips late and the response is cached), so
  // nextGameweek is checked first: right after a deadline the freshly live
  // gameweek can still sit in nextGameweek. Both slots verify the deadline
  // has really passed, falling to the numberless copy otherwise, so the
  // message never names a deadline that is still in the future.
  const currentGameweek = context?.currentGameweek ?? null;
  const nextGameweek = context?.nextGameweek ?? null;
  const lastDeadlineGameweekNumber = hasDeadlinePassed(nextGameweek)
    ? nextGameweek.number
    : hasDeadlinePassed(currentGameweek)
    ? currentGameweek.number
    : null;

  const isPanelBusy = isSearching || importingEntryId !== null;

  useEffect(() => {
    onBusyChange?.(isPanelBusy);
  }, [isPanelBusy, onBusyChange]);

  const resetState = () => {
    setInputValue('');
    setSearchError(null);
    setIsTeamNotFoundError(false);
    setIsSearching(false);
    setResults(null);
    setImportingEntryId(null);
  };

  // Backend error messages (e.g. "an unexpected error occurred") are not
  // user-appropriate, so map everything to friendly copy here. Only
  // not-found gets the deadline explanation: a timeout or server error says
  // nothing about when the team was created, and steering those at "try
  // after the next deadline" would send a member away for a week over a
  // transient failure.
  const handleImportError = (error: unknown) => {
    if (isPlannerNotFound(error)) {
      setSearchError(getTeamNotFoundMessage(lastDeadlineGameweekNumber));
      setIsTeamNotFoundError(true);
      return;
    }
    setSearchError(
      isTimeoutError(error) ? IMPORT_TIMED_OUT_MESSAGE : IMPORT_FAILED_MESSAGE
    );
    setIsTeamNotFoundError(false);
  };

  const importEntry = async (fplTeamId: number) => {
    setImportingEntryId(fplTeamId);
    try {
      await onImport(fplTeamId);
      resetState();
      onImported?.();
    } catch (error) {
      handleImportError(error);
    } finally {
      setImportingEntryId(null);
    }
  };

  const handleSubmit = async () => {
    const trimmed = inputValue.trim();
    if (!trimmed) {
      setSearchError('Enter an FPL ID or team name');
      return;
    }

    if (isFplId(trimmed)) {
      await importEntry(Number(trimmed));
      return;
    }

    if (trimmed.length < MIN_TEAM_NAME_LENGTH) {
      setSearchError('Enter at least 3 characters to search by team name');
      return;
    }

    setIsSearching(true);
    setSearchError(null);
    setIsTeamNotFoundError(false);
    setResults(null);
    try {
      const entries = await searchFplEntries({ teamName: trimmed });
      setResults(entries);
    } catch {
      setSearchError('Search failed. Please try again.');
    } finally {
      setIsSearching(false);
    }
  };

  const trimmedValue = inputValue.trim();
  const isNumericInput = trimmedValue.length > 0 && isFplId(trimmedValue);
  const isBusy = isPanelBusy || isImporting;
  const submitLabel = isSearching
    ? 'Searching...'
    : isImporting
    ? 'Importing...'
    : isNumericInput
    ? 'Import team'
    : 'Search';

  return (
    <div className="flex flex-col gap-4" data-cy="team-import-panel">
      <BodyRegular textColor="text-neutral-700">{description}</BodyRegular>

      <SearchInput
        id="team-import-input"
        label="FPL ID or team name"
        value={inputValue}
        handleChange={(event) => {
          setInputValue(event.target.value);
          if (searchError) {
            setSearchError(null);
            setIsTeamNotFoundError(false);
          }
        }}
        onKeyDown={(event) => {
          // Mirrors the submit button, including its busy guard, so enter
          // cannot queue a second search or import over an in-flight one.
          if (event.key !== 'Enter' || isBusy) return;
          event.preventDefault();
          handleSubmit();
        }}
        showCloseIcon={inputValue !== ''}
        onClose={() => setInputValue('')}
        placeholder="e.g. 1234567"
        showSearchIcon
      />

      {helpText && (
        <CaptionRegular className="text-neutral-600">{helpText}</CaptionRegular>
      )}

      {searchError && (
        <BodySmallRegular
          textColor="text-danger-700"
          data-cy="team-import-error"
        >
          {searchError}
          {isTeamNotFoundError && (
            <>
              {' In the meantime you can still read our '}
              <Link
                href={LATEST_ARTICLES_PATH}
                className="underline"
                data-cy="team-import-error-articles-link"
              >
                latest articles
              </Link>
              .
            </>
          )}
        </BodySmallRegular>
      )}

      <Button
        variant="primary"
        onClick={handleSubmit}
        disabled={isBusy}
        data-cy="team-import-submit"
      >
        {submitLabel}
      </Button>

      {results !== null && (
        <div className="flex flex-col gap-2 mt-2" data-cy="team-import-results">
          <BodySmallRegularBold>Search results</BodySmallRegularBold>
          {results.length === 0 ? (
            <BodySmallRegular textColor="text-neutral-600">
              No teams matched that search.
            </BodySmallRegular>
          ) : (
            results.map((entry) => (
              <div
                key={entry.fplEntryId}
                className="flex items-center justify-between gap-2 border border-neutral-200 rounded-lg px-3 py-2"
                data-cy={`team-import-result-${entry.fplEntryId}`}
              >
                <div className="flex flex-col min-w-0">
                  <BodySmallRegularBold className="truncate">
                    {entry.managerName}
                  </BodySmallRegularBold>
                  <CaptionRegular className="text-neutral-600 truncate">
                    {entry.teamName} - #{entry.fplEntryId}
                  </CaptionRegular>
                </div>
                <Button
                  variant="secondary"
                  onClick={() => importEntry(entry.fplEntryId)}
                  disabled={isBusy}
                  data-cy={`team-import-select-${entry.fplEntryId}`}
                >
                  {importingEntryId === entry.fplEntryId
                    ? 'Importing...'
                    : 'Select'}
                </Button>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};
